import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const CoreValuesContainer = styled.div`
  padding: 140px 0 60px;
  
  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #0a1628, #1a2744);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(10, 22, 40, 0.85), rgba(26, 39, 68, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  
    color: #ffffff;}
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  
    color: #ffffff;}
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Values Grid
const ValuesSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .values-grid .value-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .values-grid .value-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.2s both; }
    .values-grid .value-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .values-grid .value-card:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.4s both; }
    .values-grid .value-card:nth-child(5) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
    .values-grid .value-card:nth-child(6) { animation: ${scaleIn} 0.6s ease-out 0.6s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 50px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ValueCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  border: 1px solid rgba(10, 22, 40, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #0a1628, #1a2744);
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 25px 50px rgba(10, 22, 40, 0.15);
    border-color: rgba(10, 22, 40, 0.15);
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
  
  .icon {
    font-size: 4rem;
    color: #0a1628;
    margin-bottom: 30px;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.2);
    color: #1a2744;
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.7;
    font-size: 1.1rem;
    
  }
`;

// Culture Section
const CultureSection = styled.section`
  background: linear-gradient(135deg, #0a1628 0%, #1a2744 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  h2 {
    font-size: 3.5rem;
    
    color: white;
    margin-bottom: 30px;
    letter-spacing: -1.5px;
  }
  
  p {
    font-size: 1.4rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    max-width: 800px;
    margin: 0 auto;
    
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const CoreValues = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: "⭐",
      title: t('coreValues.val1Title'),
      description: t('coreValues.val1Desc')
    },
    {
      icon: "🤝",
      title: t('coreValues.val2Title'),
      description: t('coreValues.val2Desc')
    },
    {
      icon: "🌱",
      title: t('coreValues.val3Title'),
      description: t('coreValues.val3Desc')
    },
    {
      icon: "💡",
      title: t('coreValues.val4Title'),
      description: t('coreValues.val4Desc')
    },
    {
      icon: "🎯",
      title: t('coreValues.val5Title'),
      description: t('coreValues.val5Desc')
    },
    {
      icon: "👥",
      title: t('coreValues.val6Title'),
      description: t('coreValues.val6Desc')
    }
  ];

  return (
    <CoreValuesContainer>
      <Container>
        <PageTitle>{t('coreValues.title')}</PageTitle>
        <PageSubtitle>
          {t('coreValues.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('coreValues.heroTitle')}</h2>
            <p>
              {t('coreValues.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <ValuesSection 
          id="values-section" 
          data-animate
          className={animatedSections.has('values-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('coreValues.valuesTitle')}</SectionTitle>
          <ValuesGrid className="values-grid">
            {values.map((value, index) => (
              <ValueCard key={index} className="value-card">
                <div className="icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </ValueCard>
            ))}
          </ValuesGrid>
        </ValuesSection>

        <CultureSection 
          id="culture-section" 
          data-animate
          className={animatedSections.has('culture-section') ? 'animate-in' : ''}
        >
          <h2>{t('coreValues.cultureTitle')}</h2>
          <p>
            {t('coreValues.cultureDesc')}
          </p>
        </CultureSection>
      </Container>
    </CoreValuesContainer>
  );
};

export default CoreValues;